import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	Grid,
	TextField,
	InputAdornment,
	IconButton,
	Chip,
	Menu,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	alpha,
	Select,
	FormControl,
	InputLabel,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import {
	Add,
	Http,
	Search,
	MoreVert,
	PlayArrow,
	Edit,
	Delete,
	Star,
	StarBorder,
	Folder,
	CheckCircle,
	Error,
	Schedule,
	FiberManualRecord,
	Repeat,
	Visibility,
	ContentCopy,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useSnackbar } from "notistack";
import testService from "../services/testService";
import RecordTestDialog from "../components/RecordTestDialog";
import CloneDialog from "../components/CloneDialog";
import OwnerBadge from "../components/OwnerBadge";
import { canModify } from "../utils/permissions";

// The Tests page (browser tests, mode "ui") and the APIs page (API tests, mode "api")
export default function Tests({ mode = "ui" }) {
	const isApi = mode === "api";
	const base = isApi ? "/apis" : "/tests"; // this list
	const editorPath = isApi ? "/api-tests" : "/tests"; // + /:id or /new
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { enqueueSnackbar } = useSnackbar();
	const [tests, setTests] = useState([]);
	const [filteredTests, setFilteredTests] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedTest, setSelectedTest] = useState(null);
	const [cloneTarget, setCloneTarget] = useState(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [recordDialogOpen, setRecordDialogOpen] = useState(false);
	const [dataDrivenDialogOpen, setDataDrivenDialogOpen] = useState(false);
	const [dataDrivenTest, setDataDrivenTest] = useState(null);
	const [execMode, setExecMode] = useState("ITERATE_ALL");
	const [loopDialogOpen, setLoopDialogOpen] = useState(false);
	const [loopTest, setLoopTest] = useState(null);
	const [loopCount, setLoopCount] = useState(3);
	const [loopMode, setLoopMode] = useState("sequence");
	const [execRowIndex, setExecRowIndex] = useState(0);
	const [projectFilter, setProjectFilter] = useState(null);
	const [folderFilter, setFolderFilter] = useState(null);
	const [projectName, setProjectName] = useState("");
	const [folderName, setFolderName] = useState("");

	useEffect(() => {
		const projectId = searchParams.get("project");
		const folderId = searchParams.get("folder");
		setProjectFilter(projectId);
		setFolderFilter(folderId);
		loadTests();

		// Fetch project details if project filter is active
		if (projectId) {
			fetch(`/api/projects/${projectId}`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			})
				.then((res) => res.json())
				.then((data) => setProjectName(data.name || ""))
				.catch(() => setProjectName(""));
		} else {
			setProjectName("");
		}

		// Fetch folder details if folder filter is active
		if (folderId) {
			fetch(`/api/folders/${folderId}`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			})
				.then((res) => res.json())
				.then((data) => setFolderName(data.name || ""))
				.catch(() => setFolderName(""));
		} else {
			setFolderName("");
		}
	}, [searchParams]);

	useEffect(() => {
		const query = searchQuery.trim().toLowerCase();

		if (!query) {
			setFilteredTests(tests);
			return;
		}

		setFilteredTests(
			tests.filter((test) => {
				const inName = test.name?.toLowerCase().includes(query);
				const inDescription = test.description
					?.toLowerCase()
					.includes(query);
				const inTags = test.tags?.some((tag) =>
					tag.toLowerCase().includes(query),
				);

				return inName || inDescription || inTags;
			}),
		);
	}, [searchQuery, tests]);

	const loadTests = async () => {
		try {
			setLoading(true);
			const projectId = searchParams.get("project");
			const folderId = searchParams.get("folder");

			const params = { page: 0, size: 50, testType: isApi ? "API" : "UI" };
			if (projectId) params.projectId = projectId;
			if (folderId) params.folderId = folderId;

			const response = await testService.getTests(params);
			setTests(response.content || []);
		} catch (error) {
			console.error("Error loading tests:", error);
			enqueueSnackbar("Failed to load tests", { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	// A new test or API test, in the project/folder being viewed
	const newItemUrl = () => {
		const params = new URLSearchParams();
		if (searchParams.get("project")) params.set("project", searchParams.get("project"));
		if (searchParams.get("folder")) params.set("folder", searchParams.get("folder"));
		return `${editorPath}/new${params.toString() ? `?${params}` : ""}`;
	};

	const handleSearch = () => {
		const query = searchQuery.trim().toLowerCase();

		if (!query) {
			setFilteredTests(tests);
			return;
		}

		setFilteredTests(
			tests.filter((test) => {
				const inName = test.name?.toLowerCase().includes(query);
				const inDescription = test.description
					?.toLowerCase()
					.includes(query);
				const inTags = test.tags?.some((tag) =>
					tag.toLowerCase().includes(query),
				);

				return inName || inDescription || inTags;
			}),
		);
	};

	const handleExecute = async (test) => {
		// If test has data binding, show confirmation dialog
		if (test.dataBinding?.dataSetId) {
			setDataDrivenTest(test);
			setExecMode(test.dataBinding.mode || "ITERATE_ALL");
			setExecRowIndex(test.dataBinding.specificRowIndex ?? 0);
			setDataDrivenDialogOpen(true);
			return;
		}
		await runExecution(test);
	};

	const runExecution = async (test) => {
		try {
			enqueueSnackbar("Starting test execution...", { variant: "info" });
			const result = await testService.executeTest(test.id);
			enqueueSnackbar(result.message || "Test execution started", {
				variant: "success",
			});
			// Navigate to executions page with the execution ID
			navigate(`/executions?executionId=${result.executionId}`);
		} catch (error) {
			console.error("Failed to start execution", error);
			enqueueSnackbar(error?.message || "Failed to start execution", {
				variant: "error",
			});
		}
	};

	const handleDataDrivenExecute = async () => {
		setDataDrivenDialogOpen(false);
		if (dataDrivenTest) {
			try {
				enqueueSnackbar("Starting data-driven execution...", {
					variant: "info",
				});
				const result = await testService.executeTestWithData(
					dataDrivenTest.id,
					dataDrivenTest.dataBinding.dataSetId,
					execMode,
					execRowIndex,
				);
				enqueueSnackbar(result.message || "Execution started", {
					variant: "success",
				});
				navigate(`/executions?executionId=${result.executionId}`);
			} catch (error) {
				console.error("Failed to start data-driven execution", error);
				enqueueSnackbar(error?.message || "Failed to start execution", {
					variant: "error",
				});
			}
			setDataDrivenTest(null);
		}
	};

	const handleToggleFavorite = async (test) => {
		try {
			const updated = await testService.toggleFavorite(test.id);
			setTests(tests.map((t) => (t.id === test.id ? updated : t)));
			enqueueSnackbar(
				updated.metadata.isFavorite
					? "Added to favorites"
					: "Removed from favorites",
				{ variant: "success" },
			);
		} catch (error) {
			enqueueSnackbar("Failed to update favorite", { variant: "error" });
		}
	};

	const handleDelete = async () => {
		try {
			await testService.deleteTest(selectedTest.id);
			setTests(tests.filter((t) => t.id !== selectedTest.id));
			enqueueSnackbar("Test deleted successfully", {
				variant: "success",
			});
			setDeleteDialogOpen(false);
			setSelectedTest(null);
		} catch (error) {
			enqueueSnackbar("Failed to delete test", { variant: "error" });
		}
	};

	const handleSaveRecordedTest = async (testData) => {
		try {
			const savedTest = await testService.createTest(testData);
			enqueueSnackbar("Test saved successfully", { variant: "success" });
			setRecordDialogOpen(false);
			loadTests();
			return savedTest;
		} catch (error) {
			throw error;
		}
	};

	const handleMenuClick = (event, test) => {
		setAnchorEl(event.currentTarget);
		setSelectedTest(test);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleOpenLoop = (test) => {
		setLoopTest(test);
		setLoopCount(3);
		setLoopMode("sequence");
		setLoopDialogOpen(true);
		handleMenuClose();
	};

	// All iterations are one grouped run on Suite Runs; the backend queues them
	// (sequential: one after another) and runs them without this page open.
	const handleLoopExecute = async () => {
		setLoopDialogOpen(false);
		try {
			const loopRun = await testService.runLoop(
				loopTest.id,
				loopCount,
				loopMode === "parallel",
			);
			enqueueSnackbar(`Started ${loopCount} ${loopMode === "parallel" ? "parallel" : "sequential"} runs`, {
				variant: "success",
			});
			navigate(`/suite-executions?suiteExecutionId=${loopRun.id}`);
		} catch (e) {
			enqueueSnackbar(e.response?.data?.message || "Failed to start loop run", {
				variant: "error",
			});
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case "SUCCESS":
				return (
					<CheckCircle sx={{ fontSize: 18, color: "success.main" }} />
				);
			case "FAILED":
				return <Error sx={{ fontSize: 18, color: "error.main" }} />;
			default:
				return (
					<Schedule sx={{ fontSize: 18, color: "text.secondary" }} />
				);
		}
	};

	return (
		<Box>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 4,
				}}
			>
				<Box>
					<Typography variant="h4" fontWeight={700}>
						{isApi ? "APIs" : "Tests"}
					</Typography>
					{projectName || folderName ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1,
								mt: 1,
							}}
						>
							<Typography variant="body2" color="text.secondary">
								{isApi ? "All APIs" : "All Tests"}
							</Typography>
							{projectName && (
								<>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										/
									</Typography>
									<Chip
										label={projectName}
										size="small"
										color="primary"
										variant="outlined"
										onDelete={() => {
											navigate(base);
											setProjectFilter(null);
											setFolderFilter(null);
										}}
									/>
								</>
							)}
							{folderName && (
								<>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										/
									</Typography>
									<Chip
										label={folderName}
										size="small"
										color="secondary"
										variant="outlined"
										onDelete={() => {
											if (projectFilter) {
												navigate(
													`${base}?project=${projectFilter}`,
												);
											} else {
												navigate(base);
											}
											setFolderFilter(null);
										}}
									/>
								</>
							)}
						</Box>
					) : (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mt: 0.5 }}
						>
							{isApi
								? "HTTP requests with response checks, run without a browser"
								: "Create and manage your test cases"}
						</Typography>
					)}
				</Box>
				<Box sx={{ display: "flex", gap: 2 }}>
					{!isApi && (
						<Button
							variant="outlined"
							startIcon={<FiberManualRecord />}
							onClick={() => setRecordDialogOpen(true)}
							sx={{ borderRadius: 2 }}
						>
							Record Test
						</Button>
					)}
					<Button
						variant="contained"
						startIcon={isApi ? <Http /> : <Add />}
						onClick={() => navigate(newItemUrl())}
						sx={{ borderRadius: 2 }}
					>
						{isApi ? "New API Test" : "Create Test"}
					</Button>
				</Box>
			</Box>

			{/* Search Bar */}
			<TextField
				fullWidth
				placeholder={isApi ? "Search API tests by name..." : "Search tests by name..."}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				onKeyPress={(e) => e.key === "Enter" && handleSearch()}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search />
						</InputAdornment>
					),
				}}
				sx={{ mb: 3 }}
			/>

			{/* Tests Grid */}
			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress />
				</Box>
			) : filteredTests.length === 0 ? (
				<Card
					sx={{
						textAlign: "center",
						py: 8,
						background: (theme) =>
							`linear-gradient(135deg, ${alpha(
								theme.palette.primary.main,
								0.05,
							)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
					}}
				>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{isApi ? "No API tests yet" : "No tests yet"}
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 3 }}
						>
							{isApi
								? "Send HTTP requests, check the responses and pass values between them"
								: "Create your first test to get started"}
						</Typography>
						<Button
							variant="contained"
							startIcon={isApi ? <Http /> : <Add />}
							onClick={() => navigate(newItemUrl())}
						>
							{isApi ? "New API Test" : "Create Test"}
						</Button>
					</CardContent>
				</Card>
			) : (
				<Grid container spacing={3}>
					{filteredTests.map((test, index) => (
						<Grid item xs={12} md={6} lg={4} key={test.id}>
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{
									duration: 0.3,
									delay: index * 0.05,
								}}
							>
								<Card
									sx={{
										height: "100%",
										display: "flex",
										flexDirection: "column",
										cursor: "pointer",
										transition: "all 0.3s",
										"&:hover": {
											boxShadow: 6,
											transform: "translateY(-4px)",
										},
									}}
									onClick={() => {
										const projectId =
											searchParams.get("project");
										const folderId =
											searchParams.get("folder");
										let url = `${editorPath}/${test.id}`;
										const params = new URLSearchParams();
										if (projectId)
											params.set("project", projectId);
										if (folderId)
											params.set("folder", folderId);
										if (params.toString())
											url += `?${params.toString()}`;
										navigate(url);
									}}
								>
									<CardContent sx={{ flexGrow: 1 }}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "flex-start",
												mb: 2,
											}}
										>
											<Box sx={{ flexGrow: 1, pr: 1 }}>
												<Typography
													variant="h6"
													fontWeight={600}
													gutterBottom
												>
													{test.name}
												</Typography>
												{test.description && (
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{
															display:
																"-webkit-box",
															WebkitLineClamp: 2,
															WebkitBoxOrient:
																"vertical",
															overflow: "hidden",
														}}
													>
														{test.description}
													</Typography>
												)}
												<OwnerBadge item={test} sx={{ mt: 0.5 }} />
											</Box>
											<IconButton
												size="small"
												disabled={!canModify(test)}
												aria-label="Favorite"
												onClick={(e) => {
													e.stopPropagation();
													handleToggleFavorite(test);
												}}
											>
												{test.metadata?.isFavorite ? (
													<Star
														sx={{
															color: "warning.main",
														}}
													/>
												) : (
													<StarBorder />
												)}
											</IconButton>
										</Box>

										{/* Tags */}
										{test.tags && test.tags.length > 0 && (
											<Box
												sx={{
													display: "flex",
													gap: 1,
													flexWrap: "wrap",
													mb: 2,
												}}
											>
												{test.tags
													.slice(0, 3)
													.map((tag) => (
														<Chip
															key={tag}
															label={tag}
															size="small"
															sx={{
																borderRadius: 1,
																fontWeight: 500,
																fontSize:
																	"0.75rem",
															}}
														/>
													))}
												{test.tags.length > 3 && (
													<Chip
														label={`+${test.tags.length - 3}`}
														size="small"
														variant="outlined"
														sx={{ borderRadius: 1 }}
													/>
												)}
											</Box>
										)}

										{/* Folder */}
										{test.folder && (
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													mb: 2,
												}}
											>
												<Folder
													sx={{
														fontSize: 16,
														color: "text.secondary",
													}}
												/>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													{test.folder}
												</Typography>
											</Box>
										)}

										{/* Stats */}
										<Box
											sx={{
												display: "flex",
												gap: 2,
												pt: 2,
												borderTop: 1,
												borderColor: "divider",
											}}
										>
											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													{isApi ? "Requests" : "Steps"}
												</Typography>
												<Typography
													variant="body2"
													fontWeight={600}
												>
													{test.steps?.length || 0}
												</Typography>
											</Box>
											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Runs
												</Typography>
												<Typography
													variant="body2"
													fontWeight={600}
												>
													{test.metadata
														?.executionCount || 0}
												</Typography>
											</Box>
											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Status
												</Typography>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 0.5,
													}}
												>
													{getStatusIcon(
														test.lastExecutionStatus,
													)}
												</Box>
											</Box>
										</Box>
									</CardContent>

									{/* Actions */}
									<Box
										sx={{
											px: 2,
											pb: 2,
											display: "flex",
											gap: 1,
										}}
									>
										<Button
											size="small"
											variant="contained"
											startIcon={<PlayArrow />}
											onClick={(e) => {
												e.stopPropagation();
												handleExecute(test);
											}}
											sx={{ flexGrow: 1 }}
										>
											Run
										</Button>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												const projectId =
													searchParams.get("project");
												const folderId =
													searchParams.get("folder");
												let url = `${editorPath}/${test.id}`;
												const params =
													new URLSearchParams();
												if (projectId)
													params.set(
														"project",
														projectId,
													);
												if (folderId)
													params.set(
														"folder",
														folderId,
													);
												if (params.toString())
													url += `?${params.toString()}`;
												navigate(url);
											}}
											aria-label={canModify(test) ? "Edit" : "View"}
										>
											{/* Someone else's test opens read-only */}
											{canModify(test) ? <Edit /> : <Visibility />}
										</IconButton>
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												handleMenuClick(e, test);
											}}
										>
											<MoreVert />
										</IconButton>
									</Box>
								</Card>
							</motion.div>
						</Grid>
					))}
				</Grid>
			)}

			{/* Context Menu */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
			>
				<MenuItem
					onClick={() => {
						const projectId = searchParams.get("project");
						const folderId = searchParams.get("folder");
						let url = `${editorPath}/${selectedTest?.id}`;
						const params = new URLSearchParams();
						if (projectId) params.set("project", projectId);
						if (folderId) params.set("folder", folderId);
						if (params.toString()) url += `?${params.toString()}`;
						navigate(url);
						handleMenuClose();
					}}
				>
					{canModify(selectedTest) ? (
						<Edit sx={{ mr: 1, fontSize: 20 }} />
					) : (
						<Visibility sx={{ mr: 1, fontSize: 20 }} />
					)}
					{canModify(selectedTest) ? "Edit" : "View"}
				</MenuItem>
				<MenuItem onClick={() => handleOpenLoop(selectedTest)}>
					<Repeat sx={{ mr: 1, fontSize: 20 }} />
					Run in Loop
				</MenuItem>
				<MenuItem
					onClick={() => {
						setCloneTarget(selectedTest);
						handleMenuClose();
					}}
				>
					<ContentCopy sx={{ mr: 1, fontSize: 20 }} />
					Clone
				</MenuItem>
				{canModify(selectedTest) && (
					<MenuItem
						onClick={() => {
							setDeleteDialogOpen(true);
							handleMenuClose();
						}}
						sx={{ color: "error.main" }}
					>
						<Delete sx={{ mr: 1, fontSize: 20 }} />
						Delete
					</MenuItem>
				)}
			</Menu>

			<CloneDialog
				open={!!cloneTarget}
				kind="test"
				item={cloneTarget}
				onClose={() => setCloneTarget(null)}
				onCloned={(clone) => navigate(`${editorPath}/${clone.id}`)}
			/>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
			>
				<DialogTitle>Delete Test?</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete "{selectedTest?.name}"?
						This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						onClick={handleDelete}
						color="error"
						variant="contained"
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Run in Loop Dialog */}
			<Dialog
				open={loopDialogOpen}
				onClose={() => setLoopDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Run in Loop</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						{loopTest?.name}
					</Typography>
					<TextField
						label="Number of Loops"
						type="number"
						fullWidth
						value={loopCount}
						onChange={(e) => setLoopCount(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
						inputProps={{ min: 1, max: 20 }}
						helperText="Maximum 20 iterations"
						sx={{ mb: 3 }}
					/>
					<Typography variant="body2" sx={{ mb: 1 }}>
						Execution Mode
					</Typography>
					<ToggleButtonGroup
						value={loopMode}
						exclusive
						onChange={(_, val) => val && setLoopMode(val)}
						fullWidth
					>
						<ToggleButton value="sequence">
							Sequential
						</ToggleButton>
						<ToggleButton value="parallel">
							Parallel
						</ToggleButton>
					</ToggleButtonGroup>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
						{loopMode === "sequence"
							? "Each iteration starts after the previous one completes."
							: "All iterations start simultaneously."}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLoopDialogOpen(false)}>
						Cancel
					</Button>
					<Button
						onClick={handleLoopExecute}
						variant="contained"
						startIcon={<Repeat />}
					>
						{`Run ${loopCount}×`}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Record Test Dialog */}
			<RecordTestDialog
				open={recordDialogOpen}
				onClose={() => setRecordDialogOpen(false)}
				onSave={handleSaveRecordedTest}
			/>

			{/* Data-Driven Execution Confirmation Dialog */}
			<Dialog
				open={dataDrivenDialogOpen}
				onClose={() => {
					setDataDrivenDialogOpen(false);
					setDataDrivenTest(null);
				}}
			>
				<DialogTitle>Data-Driven Execution</DialogTitle>
				<DialogContent>
					<Typography sx={{ mb: 2 }}>
						This test is bound to dataset{" "}
						<strong>
							{dataDrivenTest?.dataBinding?.dataSetName ||
								"Unknown"}
						</strong>
						.
					</Typography>
					<FormControl fullWidth sx={{ mb: 2 }}>
						<InputLabel>Execution Mode</InputLabel>
						<Select
							value={execMode}
							label="Execution Mode"
							onChange={(e) => setExecMode(e.target.value)}
						>
							<MenuItem value="ITERATE_ALL">
								Iterate All Rows
							</MenuItem>
							<MenuItem value="SINGLE_ROW">Single Row</MenuItem>
							<MenuItem value="RANDOM_ROW">Random Row</MenuItem>
						</Select>
					</FormControl>
					{execMode === "SINGLE_ROW" && (
						<TextField
							label="Row Index"
							type="number"
							fullWidth
							value={execRowIndex}
							onChange={(e) =>
								setExecRowIndex(
									parseInt(e.target.value, 10) || 0,
								)
							}
							inputProps={{ min: 0 }}
							helperText="Zero-based row index in the dataset"
							sx={{ mb: 2 }}
						/>
					)}
					<Typography variant="body2" color="text.secondary">
						{execMode === "ITERATE_ALL"
							? "An execution will be created for each data row."
							: execMode === "SINGLE_ROW"
								? "A single execution will run with the specified row."
								: "A single execution will run with a randomly selected row."}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setDataDrivenDialogOpen(false);
							setDataDrivenTest(null);
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleDataDrivenExecute}
						variant="contained"
						startIcon={<PlayArrow />}
					>
						Execute
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
